'use client'

import { confirmStepUp, registerStepUpHandler } from '@/lib/api/step-up'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdLock, MdVisibility, MdVisibilityOff } from 'react-icons/md'

/**
 * Pede a senha antes de uma ação de dinheiro.
 *
 * Fica montado uma vez, no topo da aplicação, e é acionado pelo interceptor
 * HTTP quando o backend responde `STEP_UP_REQUIRED` — assim nenhuma tela
 * precisa saber que a ação é sensível: quem decide isso é o backend, no
 * decorator da rota.
 */
const INPUT_CLASS =
    'w-full h-12 pl-11 pr-11 rounded-xl border border-base-300 bg-base-100 text-[15px] text-base-content placeholder:text-base-content/40 outline-none transition-colors duration-200 focus:border-secondary focus:ring-4 focus:ring-secondary/15'

export default function StepUpModal() {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const resolveRef = useRef<((confirmed: boolean) => void) | null>(null)

    const [isOpen, setIsOpen] = useState(false)
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const finish = useCallback((confirmed: boolean) => {
        resolveRef.current?.(confirmed)
        resolveRef.current = null

        setIsOpen(false)
        setPassword('')
        setShowPassword(false)
        setError(null)
        setIsSubmitting(false)
    }, [])

    useEffect(() => {
        registerStepUpHandler(
            () =>
                new Promise<boolean>(resolve => {
                    // Uma confirmação por vez: se outra chamada cair aqui com o
                    // modal aberto, ela não fica pendurada para sempre.
                    if (resolveRef.current) {
                        resolve(false)
                        return
                    }

                    resolveRef.current = resolve
                    setIsOpen(true)
                })
        )

        return () => registerStepUpHandler(null)
    }, [])

    useEffect(() => {
        if (isOpen) {
            dialogRef.current?.showModal()
            setTimeout(() => inputRef.current?.focus(), 100)
        } else {
            dialogRef.current?.close()
        }
    }, [isOpen])

    const handleConfirm = async () => {
        if (!password.trim()) {
            setError('Informe sua senha para confirmar.')
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            await confirmStepUp(password)
            finish(true)
        } catch (err: any) {
            const message = err?.response?.data?.message

            setError(
                (Array.isArray(message) ? message[0] : message) ||
                    'Não foi possível confirmar sua senha. Tente novamente.'
            )
            setIsSubmitting(false)
        }
    }

    /**
     * ⚠️ Fechado, este componente não renderiza **nada** — e isso não é
     * economia de DOM, é o que impede o navegador de tratar o sistema inteiro
     * como uma tela de login.
     *
     * O modal é montado uma vez no `App.tsx`, então enquanto ele devolvia o
     * `<dialog>` sempre, **toda página do sistema tinha um campo de senha no
     * DOM**. O Chrome encontra esse campo, conclui que ali há um formulário de
     * login e passa a agir de acordo, em três frentes que os operadores
     * relataram como problemas separados:
     *
     * 1. Elege o campo de texto mais próximo como "usuário" e **preenche
     *    qualquer caixa com o e-mail salvo** — inclusive buscas, que então
     *    filtram a listagem para zero.
     * 2. Oferece **salvar a senha** o tempo todo, em telas onde ninguém fez
     *    login.
     * 3. Pior: preenche sozinho a senha do step-up, invisível atrás do modal
     *    fechado. A confirmação que deveria provar presença humana chegava
     *    pronta — bastava a estação destravada. Medido nesta tela: o campo
     *    estava no DOM, fora da vista e **já preenchido**, mesmo com
     *    `autoComplete="off"`.
     *
     * Sem campo de senha no DOM, nada disso tem de onde começar. É a raiz das
     * três coisas, e resolve mais que `readOnly` campo a campo.
     */
    if (!isOpen) return null

    return (
        <dialog className="modal z-50" ref={dialogRef}>
            <div className="modal-box !max-w-md !mx-4 !p-0">
                <div className="flex items-center gap-3 p-5 border-b border-base-300">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                        <MdLock size={20} />
                    </span>
                    <div>
                        <h2 className="text-lg font-bold text-base-content">
                            Confirme sua senha
                        </h2>
                        <p className="text-sm text-base-content/60">
                            Esta operação envolve dados financeiros.
                        </p>
                    </div>
                </div>

                <div className="p-6 space-y-2">
                    <label
                        htmlFor="step-up-password"
                        className="block text-sm font-medium text-base-content mb-2"
                    >
                        Senha da sua conta
                    </label>

                    <div className="relative">
                        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base-content/40">
                            <MdLock size={18} />
                        </span>

                        {/*
                         * ⚠️ `autoComplete="off"`, nunca `current-password`.
                         *
                         * Esta senha não faz login: ela confirma que quem está
                         * na frente da tela é mesmo o dono da conta, antes de o
                         * dinheiro sair. Declarada como `current-password`, ela
                         * pedia ao navegador as duas coisas que arruínam isso:
                         *
                         * 1. O Chrome **preenchia o campo sozinho** com a senha
                         *    salva — e uma confirmação que se preenche sozinha
                         *    não confirma nada; bastaria a máquina destravada.
                         * 2. Ao fechar o modal com o campo preenchido, o Chrome
                         *    lia o padrão como um login bem-sucedido e oferecia
                         *    **salvar a senha**. Como o step-up aparece em toda
                         *    ação de dinheiro, os operadores financeiros viam o
                         *    aviso o dia inteiro e se acostumavam a dispensá-lo
                         *    — que é o pior lugar para criar esse hábito.
                         */}
                        <input
                            id="step-up-password"
                            ref={inputRef}
                            type={showPassword ? 'text' : 'password'}
                            className={INPUT_CLASS}
                            value={password}
                            autoComplete="off"
                            placeholder="Digite sua senha"
                            disabled={isSubmitting}
                            onChange={event => {
                                setPassword(event.target.value)
                                setError(null)
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter') handleConfirm()
                            }}
                        />

                        <button
                            type="button"
                            onClick={() => setShowPassword(current => !current)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content/70 transition-colors"
                            tabIndex={-1}
                        >
                            {showPassword ? (
                                <MdVisibilityOff size={18} />
                            ) : (
                                <MdVisibility size={18} />
                            )}
                        </button>
                    </div>

                    {error && (
                        <p className="pt-1 text-sm text-error">{error}</p>
                    )}
                </div>

                <div className="flex justify-end gap-3 px-6 pb-6">
                    <button
                        type="button"
                        onClick={() => finish(false)}
                        disabled={isSubmitting}
                        className="btn btn-outline-light !rounded-xl"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="btn btn-secondary !border-0 !rounded-xl"
                    >
                        {isSubmitting ? 'Confirmando...' : 'Confirmar'}
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={() => finish(false)}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
